// /admin: users, machines and the waitlist (role admin only; App redirects everyone else).

import { Tag } from '../ui/Tag';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { IconAdjustmentsHorizontal, IconChevronDown, IconDots, IconKey, IconPower, IconServer, IconTrash, IconUserCheck, IconUserOff, IconUserPlus } from '@tabler/icons-react';
import { admin, type Machine, type Role, type User, type WaitlistEntry } from '../api';
import { AdminCodes } from './AdminCodes';
import { AdminInvites } from './AdminInvites';
import { AdminReports } from './AdminReports';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Select } from '../ui/Select';
import { ActionMenu } from '../ui/ActionMenu';
import { useStore } from '../store';
import { HostCard } from './HostCard';
import { BranchedMenu } from '../ui/BranchedMenu';
import { DashLayout, ago, fullDate, specs } from './Shell';
import { ChangeTier, RebootControl, UsagePanel } from './MachineControls';
import { escCloses } from '../ui/keys';
import { Empty as DsEmpty, type EmptyArtKind } from '../ui/ds';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function genPassword() {
  const alphabet = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const buf = new Uint32Array(16);
  crypto.getRandomValues(buf);
  return Array.from(buf, (n) => alphabet[n % alphabet.length]).join('');
}

function MachineSelect({
  id,
  value,
  onChange,
  machines,
  users,
  current,
  testid,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  machines: Machine[];
  users: User[];
  current?: string | null;
  testid: string;
}) {
  // Free = no owner (by machine list or by any user's assignment), plus the row's own machine.
  const taken = new Set(users.map((u) => u.machine).filter(Boolean) as string[]);
  const free = machines.filter((m) => m.id === current || (!m.owner && !taken.has(m.id)));
  return (
    <Select
      id={id}
      value={value}
      onChange={onChange}
      testid={testid}
      options={[{ value: '', label: 'No machine' }, ...free.map((m) => ({ value: m.id, label: `${m.id}${m.region ? ` · ${m.region}` : ''}${m.id === current ? ' (current)' : ''}` }))]}
    />
  );
}

function CreateUser({
  prefill,
  machines,
  users,
  onCreated,
  onCancel,
}: {
  prefill: { email: string; key: number };
  machines: Machine[];
  users: User[];
  onCreated: (u: User) => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState(prefill.email);
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [machine, setMachine] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEmail(prefill.email);
    setError(null);
    (prefill.email ? document.getElementById('nu-password') : emailRef.current)?.focus();
  }, [prefill]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const addr = email.trim();
    if (!EMAIL_RE.test(addr)) return setError('Enter a valid email address.');
    if (password.length < 10) return setError('Password must be at least 10 characters.');
    setBusy(true);
    setError(null);
    const r = await admin.createUser({ email: addr, password, role, machine: machine || null });
    setBusy(false);
    if (r.ok) {
      onCreated(r.data.user);
      setEmail('');
      setPassword('');
      setMachine('');
      setRole('member');
    } else setError(r.status === 409 ? `Conflict: ${r.error}` : r.error);
  };

  return (
    <form className="dlg-form create-user" onSubmit={submit} onKeyDown={escCloses(onCancel)} noValidate data-testid="create-user-form">
      <div className="create-user-fields">
        <div className="field">
          <label htmlFor="nu-email">Email</label>
          <input
            ref={emailRef}
            id="nu-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="nu-email"
          />
        </div>
        <div className="field">
          <label htmlFor="nu-password">Initial password</label>
          <div className="input-group">
            <input
              id="nu-password"
              type="text"
              autoComplete="off"
              spellCheck={false}
              className="mono"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="min. 10 characters"
              data-testid="nu-password"
            />
            <button type="button" className="btn btn-sm" onClick={() => setPassword(genPassword())} data-testid="nu-generate">
              Generate
            </button>
          </div>
        </div>
        <div className="field">
          <label htmlFor="nu-role">Role</label>
          <Select
            id="nu-role"
            value={role}
            onChange={(v) => setRole(v as Role)}
            testid="nu-role"
            options={[
              { value: 'member', label: 'Member' },
              { value: 'admin', label: 'Admin' },
            ]}
          />
        </div>
        <div className="field">
          <label htmlFor="nu-machine">Machine</label>
          <MachineSelect id="nu-machine" value={machine} onChange={setMachine} machines={machines} users={users} testid="nu-machine" />
        </div>
      </div>
      {error && (
        <div className="form-error" role="alert" data-testid="nu-error">
          {error}
        </div>
      )}
      <p className="small-print">Share the password with them privately; they can change it from their dashboard.</p>
      <div className="dlg-foot">
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={busy} data-testid="nu-submit">
          {busy ? 'Creating…' : 'Create user'}
        </Button>
      </div>
    </form>
  );
}

type RowMode = null | 'password' | 'machine' | 'delete';

function UserRow({
  u,
  self,
  machines,
  users,
  onChanged,
  onDeleted,
}: {
  u: User;
  self: boolean;
  machines: Machine[];
  users: User[];
  onChanged: (u: User) => void;
  onDeleted: (id: string) => void;
}) {
  const [mode, setMode] = useState<RowMode>(null);
  const [pw, setPw] = useState('');
  const [machine, setMachine] = useState(u.machine ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const open = (m: RowMode) => {
    setMode((cur) => (cur === m ? null : m));
    setError(null);
    setFlash(null);
    setPw('');
    setMachine(u.machine ?? '');
  };

  const patch = async (p: Parameters<typeof admin.updateUser>[1], done: string) => {
    setBusy(true);
    setError(null);
    const r = await admin.updateUser(u.id, p);
    setBusy(false);
    if (r.ok) {
      onChanged(r.data.user);
      setMode(null);
      setFlash(done);
    } else setError(r.error);
  };

  const del = async () => {
    setBusy(true);
    setError(null);
    const r = await admin.deleteUser(u.id);
    setBusy(false);
    if (r.ok) onDeleted(u.id);
    else setError(r.error);
  };

  const owned = machines.find((m) => m.id === u.machine);
  return (
    <>
      <tr data-testid="user-row" data-email={u.email} data-user-id={u.id} className={u.disabled ? 'is-disabled' : ''}>
        <td className="cell-head">
          <div className="cell-main">
            <span className="cell-strong">{u.email}</span>
            {self && <Tag>you</Tag>}
          </div>
        </td>
        <td data-label="Role">
          <Tag tone={u.role === 'admin' ? 'accent' : 'neutral'}>{u.role}</Tag>
        </td>
        <td data-label="Machine">
          {u.machine ? (
            <span className="cell-machine">
              <span className={`dot ${owned?.status === 'running' ? 'dot-working-static' : 'dot-exited'}`} aria-hidden="true" />
              <code>{u.machine}</code>
            </span>
          ) : (
            <span className="faint">—</span>
          )}
        </td>
        <td className="muted" data-label="Last sign-in" title={fullDate(u.lastLogin)}>
          {ago(u.lastLogin)}
        </td>
        <td data-label="Status">
          {u.disabled ? <Tag tone="red">disabled</Tag> : <Tag tone="green">active</Tag>}
        </td>
        <td className="cell-actions">
          {/* one ⋯ per row: the actions are rare, the row stays about the person */}
          <ActionMenu
            className="btn btn-sm btn-ghost btn-icon"
            ariaLabel={`Actions for ${u.email}`}
            testid="user-actions"
            label={<IconDots size={16} stroke={1.5} aria-hidden="true" />}
            items={[
              ...(self ? [] : [{ key: 'password', label: 'Reset password…', icon: <IconKey size={16} stroke={1.5} aria-hidden="true" />, testid: 'user-reset', onSelect: () => open('password') }]),
              { key: 'machine', label: 'Assign machine…', icon: <IconServer size={16} stroke={1.5} aria-hidden="true" />, testid: 'user-machine', onSelect: () => open('machine') },
              ...(self
                ? []
                : [
                    {
                      key: 'toggle',
                      label: u.disabled ? 'Enable' : 'Disable',
                      icon: u.disabled ? <IconUserCheck size={16} stroke={1.5} aria-hidden="true" /> : <IconUserOff size={16} stroke={1.5} aria-hidden="true" />,
                      testid: 'user-toggle',
                      onSelect: () => {
                        if (!busy) void patch({ disabled: !u.disabled }, u.disabled ? 'Enabled.' : 'Disabled.');
                      },
                    },
                    { key: 'delete', label: 'Delete…', icon: <IconTrash size={16} stroke={1.5} aria-hidden="true" />, danger: true, testid: 'user-delete', onSelect: () => open('delete') },
                  ]),
            ]}
          />
          {/* A modal (the top layer): it opens from the row it acts on. */}
          {mode && (
            <Dialog title={mode === 'password' ? 'Reset password' : mode === 'machine' ? 'Assign machine' : 'Delete user'} onClose={() => setMode(null)}>
              {mode === 'password' && (
                <form
                  className="dlg-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (pw.length < 10) return setError('Password must be at least 10 characters.');
                    void patch({ password: pw }, 'Password reset.');
                  }}
                >
                  <label htmlFor={`pw-${u.id}`}>New password for {u.email}</label>
                  <div className="input-group">
                    <input
                      id={`pw-${u.id}`}
                      type="text"
                      className="mono"
                      autoComplete="off"
                      spellCheck={false}
                      autoFocus
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      placeholder="min. 10 characters"
                      data-testid="user-reset-input"
                    />
                    <button type="button" className="btn btn-sm" onClick={() => setPw(genPassword())}>
                      Generate
                    </button>
                  </div>
                  {error && (
                    <div className="form-error" role="alert">
                      {error}
                    </div>
                  )}
                  <div className="dlg-foot">
                    <Button onClick={() => setMode(null)}>
                      Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={busy} data-testid="user-reset-save">
                      Save
                    </Button>
                  </div>
                </form>
              )}
              {mode === 'machine' && (
                <form
                  className="dlg-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void patch({ machine: machine || null }, machine ? `Assigned ${machine}.` : 'Machine unassigned.');
                  }}
                >
                  <label htmlFor={`m-${u.id}`}>Machine for {u.email}</label>
                  <MachineSelect
                    id={`m-${u.id}`}
                    value={machine}
                    onChange={setMachine}
                    machines={machines}
                    users={users}
                    current={u.machine}
                    testid="user-machine-select"
                  />
                  {error && (
                    <div className="form-error" role="alert">
                      {error}
                    </div>
                  )}
                  <div className="dlg-foot">
                    <Button onClick={() => setMode(null)}>
                      Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={busy || machine === (u.machine ?? '')} data-testid="user-machine-save">
                      Save
                    </Button>
                  </div>
                </form>
              )}
              {mode === 'delete' && (
                <div className="dlg-form" role="group" aria-label={`Delete ${u.email}?`}>
                  <p>
                    Delete <strong>{u.email}</strong>? Their sign-in stops working immediately
                    {u.machine ? ` and ${u.machine} becomes free` : ''}.
                  </p>
                  {error && (
                    <div className="form-error" role="alert">
                      {error}
                    </div>
                  )}
                  <div className="dlg-foot">
                    <Button onClick={() => setMode(null)} data-testid="user-delete-cancel">
                      Cancel
                    </Button>
                    <Button variant="danger" autoFocus disabled={busy} onClick={() => void del()} data-testid="user-delete-confirm">
                      {busy ? 'Deleting…' : 'Delete user'}
                    </Button>
                  </div>
                </div>
              )}
            </Dialog>
          )}
        </td>
      </tr>
      {((error && !mode) || (!mode && flash)) && (
        <tr className="row-edit">
          <td colSpan={6} onKeyDown={escCloses(() => setMode(null))}>
            {error && !mode && (
              <div className="form-error" role="alert" data-testid="user-error">
                {error}
              </div>
            )}
            {!mode && flash && (
              <div className="form-ok row-flash" role="status">
                {flash}
                <button className="icon-btn" aria-label="Dismiss" onClick={() => setFlash(null)}>
                  ×
                </button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function WaitlistRow({ e, hasAccount, onCreate, onRemoved }: { e: WaitlistEntry; hasAccount: boolean; onCreate: () => void; onRemoved: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setBusy(true);
    const r = await admin.removeWaitlist(e.email);
    setBusy(false);
    if (r.ok) onRemoved();
    else setError(r.error);
  };
  return (
    <tr data-testid="waitlist-row" data-email={e.email}>
      <td className="cell-head">
        <span className="cell-strong">{e.email}</span>
      </td>
      <td className="cell-note" data-label="Note">
        {e.note ? e.note : <span className="faint">—</span>}
      </td>
      <td className="muted" data-label="Requested" title={fullDate(e.created)}>
        {ago(e.created)}
      </td>
      <td className="cell-actions">
        {confirming ? (
          <div className="row-btns" onKeyDown={escCloses(() => setConfirming(false))}>
            <span className="confirm-text">Remove?</span>
            <button className="btn btn-sm btn-danger-solid" autoFocus disabled={busy} onClick={() => void remove()} data-testid="wl-remove-confirm">
              Remove
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <div className="row-btns">
            {hasAccount ? (
              <Tag tone="green">has account</Tag>
            ) : (
              <button className="btn btn-sm" onClick={onCreate} data-testid="wl-create">
                <IconUserPlus size={16} stroke={1.5} aria-hidden="true" />
                Create account
              </button>
            )}
            <button className="btn btn-sm btn-ghost btn-ghost-danger" onClick={() => setConfirming(true)} data-testid="wl-remove">
              <IconTrash size={16} stroke={1.5} aria-hidden="true" />
              Remove
            </button>
          </div>
        )}
        {error && (
          <div className="form-error" role="alert">
            {error}
          </div>
        )}
      </td>
    </tr>
  );
}

function DeleteMachine({ id, onDeleted, onClose }: { id: string; onDeleted: () => void; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog title="Delete machine" onClose={onClose}>
      <p>
        Delete <code>{id}</code> and its disk? This can’t be undone.
      </p>
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}
      <div className="dlg-foot">
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger"
          autoFocus
          disabled={busy}
          data-testid="delete-machine-confirm"
          onClick={async () => {
            setBusy(true);
            const r = await admin.deleteMachine(id);
            setBusy(false);
            if (r.ok) onDeleted();
            else setError(r.error);
          }}
        >
          {busy ? 'Deleting…' : 'Delete machine'}
        </Button>
      </div>
    </Dialog>
  );
}

// the rail: groups follow the page, top to bottom (Users and Waitlist are people; Machines and Host are the fleet; Codes is billing; Reports are the apps')
const RAIL: { label: string; items: [string, string][] }[] = [
  { label: 'Accounts', items: [['users', 'Users'], ['waitlist', 'Waitlist']] },
  { label: 'Machines', items: [['machines', 'Machines'], ['host', 'Host']] },
  { label: 'Billing', items: [['codes', 'Codes']] },
  { label: 'Apps', items: [['reports', 'Reports']] },
];

/** An empty table: the shared empty state (ds Empty md) with the tray. */
const Empty = ({ title, body, art = 'tray' }: { title: string; body: string; art?: EmptyArtKind }) => <DsEmpty size="md" art={art} className="table-empty" title={title} lead={body} />;

export function AdminPage() {
  const me = useStore((s) => s.me)!;
  const [users, setUsers] = useState<User[] | null>(null);
  const [machines, setMachines] = useState<Machine[] | null>(null);
  const [openMachine, setOpenMachine] = useState<string | null>(null);
  const [action, setAction] = useState<{ id: string; kind: 'size' | 'reboot' | 'delete' } | null>(null);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<{ email: string; key: number } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [hasHost, setHasHost] = useState(false);
  const rail = useMemo(() => RAIL.map((g) => ({ ...g, items: g.items.filter(([id]) => id !== 'host' || hasHost) })), [hasHost]);
  const [active, setActive] = useState('users');

  // highlight the section being read in the rail
  useEffect(() => {
    const els = rail.flatMap((g) => g.items.map(([id]) => document.getElementById(id))).filter((e): e is HTMLElement => !!e);
    const io = new IntersectionObserver(
      (entries) => {
        const seen = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (seen[0]) setActive(seen[0].target.id);
      },
      { rootMargin: '0px 0px -70% 0px' },
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, [rail, users, machines, waitlist]);

  const load = useCallback(async () => {
    const [u, m, w] = await Promise.all([admin.users(), admin.machines(), admin.waitlist()]);
    if (u.ok) setUsers(u.data.users ?? []);
    if (m.ok) setMachines(m.data.machines ?? []);
    if (w.ok) setWaitlist(w.data.entries ?? []);
    const bad = [u, m, w].find((r) => !r.ok);
    setError(bad && !bad.ok ? bad.error : null);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const emails = useMemo(() => new Set((users ?? []).map((u) => u.email.toLowerCase())), [users]);
  const ownerLabel = (owner: string | null) => {
    if (!owner) return null;
    return users?.find((u) => u.id === owner)?.email ?? owner;
  };

  const startCreate = (email = '') => {
    setNotice(null);
    setCreating({ email, key: Date.now() });
  };

  return (
    <DashLayout title="Admin" subtitle="Users, machines, the waitlist and codes.">
      <div className="admin" data-testid="admin">
        <nav className="admin-rail" aria-label="Admin sections" data-testid="admin-rail">
          <BranchedMenu
            label="Admin sections"
            active={active}
            onSelect={setActive}
            sections={rail.map((g) => ({ label: g.label, children: g.items.map(([value, label]) => ({ value, label, href: `#${value}` })) }))}
          />
        </nav>
        <div className="admin-content">
      {error && (
        <div className="banner banner-error" role="alert">
          {error}
          <button className="btn btn-sm" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      <section className="card" id="users" aria-labelledby="users-title" data-testid="admin-users">
        <div className="card-head">
          <div className="card-title">
            <h2 id="users-title">Users</h2>
            <span className="count">{users?.length ?? ''}</span>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => startCreate()} data-testid="new-user">
            + New user
          </button>
        </div>
        {creating && (
          <Dialog title="New user" wide onClose={() => setCreating(null)}>
            <CreateUser
              prefill={creating}
              machines={machines ?? []}
              users={users ?? []}
              onCancel={() => setCreating(null)}
              onCreated={(u) => {
                setCreating(null);
                setNotice(`Created ${u.email}${u.machine ? ` on ${u.machine}` : ''}.`);
                void load();
              }}
            />
          </Dialog>
        )}
        {notice && (
          <div className="banner banner-ok" role="status" data-testid="admin-notice">
            {notice}
            <button className="icon-btn" aria-label="Dismiss" onClick={() => setNotice(null)}>
              ×
            </button>
          </div>
        )}
        <div className="table-wrap table-cards">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Machine</th>
                <th scope="col">Last sign-in</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map((u) => (
                <UserRow
                  key={u.id}
                  u={u}
                  self={u.id === me.id || u.email === me.email}
                  machines={machines ?? []}
                  users={users ?? []}
                  onChanged={(nu) => {
                    setUsers((list) => list?.map((x) => (x.id === nu.id ? nu : x)) ?? null);
                    void admin.machines().then((r) => r.ok && setMachines(r.data.machines ?? []));
                  }}
                  onDeleted={(id) => {
                    setUsers((list) => list?.filter((x) => x.id !== id) ?? null);
                    void load();
                  }}
                />
              ))}
            </tbody>
          </table>
          {!users && <div className="skeleton" aria-busy="true" aria-label="Loading users" />}
        </div>
      </section>

      <section className="card" id="waitlist" aria-labelledby="waitlist-title" data-testid="admin-waitlist">
        <div className="card-head">
          <div className="card-title">
            <h2 id="waitlist-title">Waitlist</h2>
            <span className="count">{waitlist?.length ?? ''}</span>
          </div>
        </div>
        <div className="table-wrap table-cards">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Note</th>
                <th scope="col">Requested</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(waitlist ?? []).map((e) => (
                <WaitlistRow
                  key={e.email}
                  e={e}
                  hasAccount={emails.has(e.email.toLowerCase())}
                  onCreate={() => startCreate(e.email)}
                  onRemoved={() => setWaitlist((l) => l?.filter((x) => x.email !== e.email) ?? null)}
                />
              ))}
            </tbody>
          </table>
          {waitlist && !waitlist.length && <Empty art="list" title="Waitlist is empty" body="Requests from the landing page show up here." />}
          {!waitlist && <div className="skeleton" aria-busy="true" aria-label="Loading waitlist" />}
        </div>
      </section>

      <section className="card" id="machines" aria-labelledby="machines-title" data-testid="admin-machines">
        <div className="card-head">
          <div className="card-title">
            <h2 id="machines-title">Machines</h2>
            <span className="count">{machines?.length ?? ''}</span>
          </div>
        </div>
        <div className="table-wrap table-cards">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Machine</th>
                <th scope="col">Status</th>
                <th scope="col">Owner</th>
                <th scope="col">Specs</th>
                <th scope="col">Region</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(machines ?? []).map((m) => (
                <Fragment key={m.id}>
                <tr data-testid="machine-row" data-machine-id={m.id} className={openMachine === m.id ? 'is-open' : ''}>
                  <td className="cell-head">
                    <code className="cell-strong">{m.id}</code>
                  </td>
                  <td data-label="Status">
                    <Tag tone={m.status === 'running' ? 'green' : 'red'}>
                      <span className={`dot ${m.status === 'running' ? 'dot-working-static' : 'dot-exited'}`} aria-hidden="true" />
                      {m.status}
                    </Tag>
                  </td>
                  <td data-label="Owner">{ownerLabel(m.owner) ?? <Tag tone="accent">free</Tag>}</td>
                  <td className="muted" data-label="Specs">
                    {specs(m)}
                  </td>
                  <td className="muted" data-label="Region">
                    {m.region || '—'}
                  </td>
                  <td className="cell-actions">
                    <button
                      className="btn btn-sm btn-ghost btn-icon"
                      aria-expanded={openMachine === m.id}
                      aria-label={openMachine === m.id ? 'Hide usage' : 'Show usage'}
                      onClick={() => setOpenMachine(openMachine === m.id ? null : m.id)}
                      data-testid="machine-usage"
                    >
                      <IconChevronDown size={16} stroke={1.5} aria-hidden="true" className={openMachine === m.id ? 'is-flipped' : ''} />
                    </button>
                    <ActionMenu
                      className="btn btn-sm btn-ghost"
                      testid="machine-manage"
                      label={
                        <>
                          <IconAdjustmentsHorizontal size={16} stroke={1.5} aria-hidden="true" />
                          Manage
                        </>
                      }
                      items={[
                        { key: 'size', label: 'Change size', icon: <IconAdjustmentsHorizontal size={16} stroke={1.5} aria-hidden="true" />, testid: 'machine-change-size', onSelect: () => setAction({ id: m.id, kind: 'size' }) },
                        { key: 'reboot', label: 'Restart…', icon: <IconPower size={16} stroke={1.5} aria-hidden="true" />, testid: 'machine-reboot', onSelect: () => setAction({ id: m.id, kind: 'reboot' }) },
                        { key: 'delete', label: 'Delete', icon: <IconTrash size={16} stroke={1.5} aria-hidden="true" />, danger: true, testid: 'machine-delete', onSelect: () => setAction({ id: m.id, kind: 'delete' }) },
                      ]}
                    />
                  </td>
                </tr>
                {openMachine === m.id && (
                  <tr className="manage-row">
                    <td colSpan={6}>
            <div className="machine-manage" data-testid="machine-manage-panel">
              <UsagePanel machineId={m.id} compact />
            </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {action?.kind === 'size' && <ChangeTier machineId={action.id} dialog={{ onClose: () => setAction(null) }} onChanged={() => void load()} />}
          {action?.kind === 'reboot' && <RebootControl machineId={action.id} asAdmin dialog={{ onClose: () => setAction(null) }} onDone={() => void load()} />}
          {action?.kind === 'delete' && (
            <DeleteMachine
              id={action.id}
              onClose={() => setAction(null)}
              onDeleted={() => {
                setAction(null);
                setOpenMachine(null);
                void load();
              }}
            />
          )}
          {machines && !machines.length && <Empty art="machine" title="No machines" body="Machines register with the hub when their agent starts." />}
          {!machines && <div className="skeleton" aria-busy="true" aria-label="Loading machines" />}
        </div>
      </section>

          <HostCard onPresence={setHasHost} />
          <AdminInvites />
          <AdminCodes />
          <AdminReports />
        </div>
      </div>
    </DashLayout>
  );
}
